import * as React from 'react';
import { Route, Switch, useHistory, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
    AppBar,
    Box,
    CssBaseline,
    Divider,
    Drawer,
    IconButton,
    Toolbar,
    Typography
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuIcon from '@mui/icons-material/Menu';

import LocationDetails from '../components/LocationDetails';
import LocationsList from '../components/LocationsList';
import LocationsMap from '../components/LocationsMap';

const drawerWidth = 350;

function LocationsDrawerController(props) {
    const { window, locations, currentLanguage } = props;
    let history = useHistory();
    const { id } = useParams();

    const [mobileOpen, setMobileOpen] = React.useState(false);

    const [currentLocation, setCurrentLocation] = React.useState(() => {
        if (id) {
            // eslint-disable-next-line
            const location = locations.filter(location => location.id == id)[0];
            setMobileOpen(true);
            return location;
        }
        return null;
    });

    React.useEffect(() => {
        if (!id) {
            setCurrentLocation(null);
        }
    }, [id]);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleLocationSelection = function(location) {
        setCurrentLocation(location);
        setMobileOpen(true);
        history.push(`/${location.id}/${location.address.city}`);
    };

    const drawer = (
        <div>
            <Toolbar
                sx={{
                    display: { sm: 'none' },
                    justifyContent: 'flex-end'
                }}
            >
                <IconButton
                    color="inherit"
                    aria-label="close drawer"
                    edge="end"
                    onClick={handleDrawerToggle}
                    sx={{ display: { sm: 'none' } }}
                >
                    <ChevronLeftIcon />
                </IconButton>
            </Toolbar>
            <Divider
                sx={{
                    display: { sm: 'none' }
                }}
            />
            <Switch>
                <Route path="/" exact>
                    <LocationsList locations={locations} handleLocationSelection={handleLocationSelection} />
                </Route>
                <Route path="/:id/:city">
                    <LocationDetails locations={locations} />
                </Route>
            </Switch>
        </div>
    );

    const container = window !== undefined ? () => window().document.body : undefined;

    return (
        <Box sx={{ display: 'flex', height: '850px' }}>
            <CssBaseline />
            <AppBar
                position="fixed"
                color="inherit"
                sx={{
                    top: '50px',
                    zIndex: 10,
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    ml: { sm: `${drawerWidth}px` },
                    display: { sm: 'none' }
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography
                        variant="h6"
                        noWrap component="div"
                    >
                        Standorte
                    </Typography>
                </Toolbar>
            </AppBar>
            <Box
                component="nav"
                sx={{ height: '850px', width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
                aria-label="locations"
            >
                {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
                <Drawer
                    container={container}
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', height: '100%', width: drawerWidth, mb: '20px' },
                    }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', height: { sm: `850px` }, width: drawerWidth, position: 'relative' },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 0,
                    height: { sm: `calc(100%)` },
                    width: { sm: `calc(100% - ${drawerWidth}px)` }
                }}
            >
                <Toolbar sx={{
                    display: { sm: 'none' }
                }} />
                <LocationsMap locations={locations} currentLocation={currentLocation} handleLocationSelection={handleLocationSelection} currentLanguage={currentLanguage} />
            </Box>
        </Box>
    );
}

LocationsDrawerController.propTypes = {
    /**
     * Injected by the documentation to work in an iframe.
     * You won't need it on your project.
     */
    window: PropTypes.func,

    locations: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        address: PropTypes.shape({
            postcode: PropTypes.string.isRequired,
            city: PropTypes.string.isRequired,
            country: PropTypes.shape({
                title: PropTypes.string.isRequired
            }),
            state: PropTypes.string,
            street: PropTypes.string.isRequired,
        }),
        name: PropTypes.string.isRequired,
        notice: PropTypes.string,
        websiteUrl: PropTypes.string,
        coordinates: PropTypes.shape({
            lat: PropTypes.number.isRequired,
            lng: PropTypes.number.isRequired
        }),
        contact: PropTypes.shape({
            phone: PropTypes.string,
            email: PropTypes.string.isRequired,
        }),
        franchisePartner: PropTypes.shape({
            firstname: PropTypes.string.isRequired,
            fullImageUrl: PropTypes.string, // e.g. "https://localhost:8000/media/file-download/RnJhbmNoaXNlUGFydG5lckltYWdlcy9hdC1zZWJhc3RpYW4tenVnbWF5ZXItMjU3eDMwMC5qcGVn"
            // imageUrl: "/media/file-download/RnJhbmNoaXNlUGFydG5lckltYWdlcy9hdC1zZWJhc3RpYW4tenVnbWF5ZXItMjU3eDMwMC5qcGVn"
            lastname: PropTypes.string.isRequired,
        }),
    })).isRequired
};

export default LocationsDrawerController;